<template>
  <div class="product-list">
    <page-header title="商品列表"></page-header>
    
    <el-card>
      <!-- 搜索区域 -->
      <div class="search-area">
        <el-form :inline="true" :model="searchForm">
          <el-form-item>
            <el-input
              v-model="searchForm.keyword"
              placeholder="商品名称/编码"
              clearable>
            </el-input>
          </el-form-item>
          <el-form-item>
            <el-select v-model="searchForm.category" placeholder="商品分类" clearable>
              <el-option
                v-for="item in categories"
                :key="item.value"
                :label="item.label"
                :value="item.value">
              </el-option>
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" @click="handleSearch">搜索</el-button>
            <el-button @click="handleReset">重置</el-button>
          </el-form-item>
        </el-form>
      </div>
      
      <!-- 表格区域 -->
      <el-table
        :data="tableData"
        v-loading="loading"
        border
        style="width: 100%">
        <el-table-column prop="code" label="商品编码" width="120"></el-table-column>
        <el-table-column prop="name" label="商品名称"></el-table-column>
        <el-table-column prop="category" label="分类" width="120">
          <template slot-scope="scope">
            {{ getCategoryLabel(scope.row.category) }}
          </template>
        </el-table-column>
        <el-table-column prop="stock" label="当前库存" width="100"></el-table-column>
        <el-table-column prop="threshold" label="预警阈值" width="100"></el-table-column>
        <el-table-column label="状态" width="100">
          <template slot-scope="scope">
            <status-tag
              :status="getStockStatus(scope.row)"
              :status-map="stockStatusMap">
            </status-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="200" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button type="text" @click="handleStockIn(scope.row)">入库</el-button>
            <el-button type="text" @click="handleStockOut(scope.row)">出库</el-button>
          </template>
        </el-table-column>
      </el-table>
      
      <!-- 分页 -->
      <div class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="page.current"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="page.size"
          layout="total, sizes, prev, pager, next, jumper"
          :total="page.total">
        </el-pagination>
      </div>
    </el-card>
    
    <!-- 商品表单对话框 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="600px">
      <product-form
        :initial-data="currentProduct"
        @submit="handleSubmit"
        @cancel="dialogVisible = false">
      </product-form>
    </el-dialog>
  </div>
</template>

<script>
import StatusTag from '@/components/common/StatusTag.vue'
import ProductForm from '@/components/product/ProductForm.vue'
import { mapActions } from 'vuex'
import PageHeader from '@/components/common/PageHeader.vue'

export default {
  name: 'ProductList',
  components: {
    StatusTag,
    ProductForm,
    PageHeader
  },
  data() {
    return {
      searchForm: {
        keyword: '',
        category: ''
      },
      loading: false,
      tableData: [],
      page: {
        current: 1,
        size: 10,
        total: 0
      },
      dialogVisible: false,
      currentProduct: null,
      categories: [
        { value: 'electronics', label: '电子产品' },
        { value: 'clothing', label: '服装' },
        { value: 'food', label: '食品' }
      ],
      stockStatusMap: {
        normal: { type: 'success', label: '正常' },
        warning: { type: 'warning', label: '预警' },
        empty: { type: 'danger', label: '无货' }
      }
    }
  },
  computed: {
    dialogTitle() {
      return this.currentProduct ? '编辑商品' : '新增商品'
    }
  },
  created() {
    this.fetchData()
  },
  methods: {
    ...mapActions('product', ['getProducts', 'saveProduct']),
    
    async fetchData() {
      this.loading = true
      try {
        const { items, total } = await this.getProducts({
          ...this.searchForm,
          page: this.page.current,
          size: this.page.size
        })
        this.tableData = items
        this.page.total = total
      } finally {
        this.loading = false
      }
    },
    
    getCategoryLabel(value) {
      return this.categories.find(item => item.value === value)?.label
    },
    
    getStockStatus(row) {
      if (row.stock === 0) return 'empty'
      if (row.stock < row.threshold) return 'warning'
      return 'normal'
    },
    
    handleSearch() {
      this.page.current = 1
      this.fetchData()
    },
    
    handleReset() {
      this.searchForm = {
        keyword: '',
        category: ''
      }
      this.handleSearch()
    },
    
    handleSizeChange(val) {
      this.page.size = val
      this.fetchData()
    },
    
    handleCurrentChange(val) {
      this.page.current = val
      this.fetchData()
    },
    
    handleAdd() {
      this.currentProduct = null
      this.dialogVisible = true
    },
    
    handleEdit(row) {
      this.currentProduct = { ...row }
      this.dialogVisible = true
    },
    
    async handleSubmit(data) {
      try {
        await this.saveProduct(data)
        this.$message.success('保存成功')
        this.dialogVisible = false
        this.fetchData()
      } catch (error) {
        this.$message.error('保存失败')
      }
    },
    
    handleStockIn(row) {
      this.$router.push({
        path: '/inventory/in',
        query: { productId: row.id }
      })
    },
    
    handleStockOut(row) {
      this.$router.push({
        path: '/inventory/out',
        query: { productId: row.id }
      })
    }
  }
}
</script>

<style scoped>
.product-list {
  padding: 20px;
}

.search-area {
  margin-bottom: 20px;
}

.pagination-container {
  margin-top: 20px;
  text-align: right;
}
</style> 